import React, { useState, useEffect, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { Dialog } from "primereact/dialog";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { AuthContext } from "../../../../Header/auth - context";
import { uzmiGresku } from "../../../../custom/greske";
import "./VozilaProdaja.css";

/* Prodaja vozila — vozila sa izlaznih faktura grupe 334 („Faktura prodaja vozila").

   Za svako prodato vozilo jedna radnja, zavisno od kupca:
     kupac je firma u sistemu  → PRENOS: kod nas vozilo i svi podaci idu u arhivu
                                 i Status 0, kod kupca nastaje kopija sa svim podacima.
     kupac nije u sistemu      → ODJAVA: samo arhiva i Status 0.
   Kupac se prepoznaje po PIB-u (vehicle.menadzerProdajaVozila). Svaki prenos ostaje
   zapisan, pa se iz njega vidi istorija vozila kroz firme. */

const STANJA = {
    /* Faktura još nije proverena — prenos se ne nudi dok je neko ne proveri. */
    PROVERA:   { kljuc: "admin.prodVozStanjeProvera",   klasa: "provera" },
    CEKA:      { kljuc: "admin.prodVozStanjeCeka",      klasa: "ceka" },
    PRENOS:    { kljuc: "admin.prodVozStanjePreneto",   klasa: "preneto" },
    ODJAVA:    { kljuc: "admin.prodVozStanjeOdjava",    klasa: "odjava" },
    NEAKTIVNO: { kljuc: "admin.prodVozStanjeNeaktivno", klasa: "neaktivno" },
};

const VozilaProdaja = () => {
    const { t } = useTranslation();
    const toast = useRef(null);
    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);

    const [podaci, setPodaci] = useState([]);
    const [ucitava, setUcitava] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [selectedId, setSelectedId] = useState(null);     // `${id_faktura}-${id_vozilo}`
    const [napomena, setNapomena] = useState("");
    const [salje, setSalje] = useState(false);
    const [istorija, setIstorija] = useState(null);          // { naslov, dogadjaji: [] }

    const kljucReda = (r) => `${r.id_faktura}-${r.id_vozilo}`;
    const izabran = podaci.find(r => kljucReda(r) === selectedId) || null;

    const zovi = async (telo) => {
        const res = await fetch(process.env.REACT_APP_BACKEND_URL + "/vehicles/ManageVehicleSale", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify(telo),
        });
        if (!res.ok) throw res;
        return res.json();
    };

    const ucitaj = async () => {
        setUcitava(true);
        try {
            const data = await zovi({ step: "INIT" });
            setPodaci(Array.isArray(data) ? data : []);
        } catch (e) {
            const g = await uzmiGresku(e, t("admin.prodVozGreskaUcitavanje"));
            toast.current?.show({ severity: "error", summary: t("common.greska"), detail: g.message, life: 5000 });
        } finally {
            setUcitava(false);
        }
    };

    useEffect(() => { ucitaj(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    /* Toggle izbora kroz funkcionalni update — DataTable namerno nema onRowClick
       (vidi CLAUDE.md: dvostruko okidanje bi pokvarilo toggle). */
    const handleRowSelect = (row) => {
        const id = kljucReda(row);
        setSelectedId(prev => {
            const next = prev === id ? null : id;
            setNapomena("");
            return next;
        });
    };

    const posaljiPrenos = async () => {
        if (!izabran) return;
        setSalje(true);
        try {
            const odg = await zovi({
                step: "SAVE_PRENOS",
                id_faktura: izabran.id_faktura,
                id_vozilo: izabran.id_vozilo,
                napomena: napomena.trim() || null,
            });
            toast.current?.show({
                severity: "success",
                summary: t("admin.m_uspesno"),
                detail: odg?.Nacin === "PRENOS" ? t("admin.prodVozPreneto") : t("admin.prodVozOdjavljeno"),
                life: 4000,
            });
            setSelectedId(null);
            setNapomena("");
            await ucitaj();
        } catch (e) {
            const g = await uzmiGresku(e, t("admin.prodVozGreskaPrenos"));
            toast.current?.show({ severity: "error", summary: t("common.greska"), detail: g.message, life: 6000 });
        } finally {
            setSalje(false);
        }
    };

    const potvrdiPrenos = () => {
        if (!izabran) return;
        const jePrenos = !!izabran.kupac_firma_id;
        confirmDialog({
            message: jePrenos
                ? t("admin.prodVozPotvrdaPrenos", { vozilo: izabran.Registracija, firma: izabran.KupacFirma })
                : t("admin.prodVozPotvrdaOdjava", { vozilo: izabran.Registracija }),
            header: jePrenos ? t("admin.prodVozPrenesi") : t("admin.prodVozOdjavi"),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("common.yes"),
            rejectLabel: t("common.no"),
            acceptClassName: jePrenos ? "p-button-success" : "p-button-danger",
            accept: posaljiPrenos,
        });
    };

    const otvoriIstoriju = async (row) => {
        try {
            const data = await zovi({ step: "ISTORIJA", id_vozilo: row.id_vozilo });
            setIstorija({
                naslov: [row.Registracija, row.Marka, row.Model].filter(Boolean).join(" · "),
                sasija: row.Sasija,
                dogadjaji: Array.isArray(data) ? data : [],
            });
        } catch (e) {
            const g = await uzmiGresku(e, t("admin.prodVozGreskaUcitavanje"));
            toast.current?.show({ severity: "error", summary: t("common.greska"), detail: g.message, life: 5000 });
        }
    };

    const oznakaStanja = (stanje) => {
        const s = STANJA[stanje] || STANJA.NEAKTIVNO;
        return <span className={`prodvoz-stanje ${s.klasa}`}>{t(s.kljuc)}</span>;
    };

    /* ── Excel ─────────────────────────────────────────────────────────────── */
    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Prodaja vozila");
        const headers = [
            t("admin.prodVozFaktura"), t("admin.prodVozDatum"), t("admin.prodVozKupac"),
            t("admin.prodVozRegistracija"), t("admin.marka"), t("admin.model"), t("admin.sasija"),
            t("admin.prodVozStanje"), t("admin.prodVozKupacFirma"), t("admin.prodVozDatumPrenosa"), t("admin.prodVozPreneo"),
        ];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
            cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
        });
        (podaci || []).forEach(r => {
            const row = worksheet.addRow([
                r.BrojFakture || "", r.DatumFakture || "", r.Kupac || "",
                r.Registracija || "", r.Marka || "", r.Model || "", r.Sasija || "",
                t((STANJA[r.Stanje] || STANJA.NEAKTIVNO).kljuc), r.KupacFirma || "",
                r.DatumPrenosa || "", r.Preneo || "",
            ]);
            row.eachCell(cell => {
                cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
            });
        });
        worksheet.columns.forEach(col => {
            let max = 10;
            col.eachCell(cell => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString("sr-RS", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade",
        }).replace(/,/g, "");
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `Prodaja vozila - ${company?.companyName || ""} - ${dt}.xlsx`);
    };

    const tableHeader = (
        <div className="flex align-items-center gap-2 export-buttons" style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: "0.4rem" }}>
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                {izabran && (
                    <Button type="button" icon="pi pi-history" severity="info" rounded
                            onClick={() => otvoriIstoriju(izabran)} data-pr-tooltip={t("admin.prodVozIstorija")} />
                )}
            </div>
            <input type="search" placeholder={t("common.search")} className="p-inputtext p-component"
                   onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    const cekaPrenos = izabran?.Stanje === "CEKA";
    const jePrenos = !!izabran?.kupac_firma_id;

    return (
        <div className="col-12">
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="blagajnaDashboard dnevniceDashboard">
                {/* ── Izabrano vozilo i radnja ── */}
                <Card>
                    {!izabran ? (
                        <p className="infoPoruka prodvoz-info">{t("admin.prodVozIzaberiRed")}</p>
                    ) : (
                        <div className="prodvoz-detalj">
                            <div className="prodvoz-detalj-vozilo">
                                <span className="prodvoz-registracija">{izabran.Registracija}</span>
                                <span className="prodvoz-opis">{[izabran.Marka, izabran.Model].filter(Boolean).join(" ")}</span>
                                <span className="prodvoz-opis">{t("admin.sasija")}: {izabran.Sasija || "-"}</span>
                                {oznakaStanja(izabran.Stanje)}
                            </div>
                            <div className="prodvoz-detalj-red">
                                <span>{t("admin.prodVozFaktura")}: <strong>{izabran.BrojFakture}</strong> · {izabran.DatumFakture}</span>
                                <span>{t("admin.prodVozKupac")}: <strong>{izabran.Kupac}</strong>{izabran.KupacPIB ? ` · ${izabran.KupacPIB}` : ""}</span>
                            </div>

                            {cekaPrenos ? (
                                <>
                                    <div className={`prodvoz-ishod ${jePrenos ? "prenos" : "odjava"}`}>
                                        <i className={`pi ${jePrenos ? "pi-arrow-right-arrow-left" : "pi-sign-out"}`} />
                                        <span>
                                            {jePrenos
                                                ? t("admin.prodVozIshodPrenos", { firma: izabran.KupacFirma })
                                                : t("admin.prodVozIshodOdjava")}
                                        </span>
                                    </div>
                                    <label htmlFor="prodvoz-napomena">{t("admin.prodVozNapomena")}:</label>
                                    <textarea
                                        id="prodvoz-napomena"
                                        className="p-inputtext p-component prodvoz-napomena"
                                        rows={2}
                                        maxLength={500}
                                        value={napomena}
                                        onChange={e => setNapomena(e.target.value)}
                                    />
                                    <button type="button" onClick={potvrdiPrenos} disabled={salje}
                                            style={{ minWidth: "20%", marginTop: "2%" }}
                                            className={`btn ${jePrenos ? "btn-success" : "btn-danger"}`}>
                                        {salje
                                            ? <i className="pi pi-spin pi-spinner" />
                                            : (jePrenos ? t("admin.prodVozPrenesi") : t("admin.prodVozOdjavi"))}
                                    </button>
                                </>
                            ) : (
                                <div className="prodvoz-detalj-red">
                                    {izabran.DatumPrenosa && (
                                        <span>{t("admin.prodVozDatumPrenosa")}: <strong>{izabran.DatumPrenosa}</strong>{izabran.Preneo ? ` · ${izabran.Preneo}` : ""}</span>
                                    )}
                                    {izabran.Stanje === "PRENOS" && izabran.KupacFirma && (
                                        <span>{t("admin.prodVozKupacFirma")}: <strong>{izabran.KupacFirma}</strong></span>
                                    )}
                                    {izabran.Stanje === "NEAKTIVNO" && <span>{t("admin.prodVozNeaktivnoOpis")}</span>}
                                    {izabran.Stanje === "PROVERA" && (
                                        <span className="prodvoz-ishod odjava">
                                            <i className="pi pi-lock" /> {t("admin.prodVozNijeProverena")}
                                        </span>
                                    )}
                                    {izabran.Napomena && <span>{t("admin.prodVozNapomena")}: {izabran.Napomena}</span>}
                                </div>
                            )}
                        </div>
                    )}
                </Card>

                {/* ── Spisak ── */}
                <Card>
                    <Tooltip target=".export-buttons>button" position="bottom" />
                    <DataTable
                        value={podaci || []}
                        loading={ucitava}
                        paginator rows={10}
                        globalFilter={globalFilter}
                        scrollable scrollHeight="50vh"
                        header={tableHeader}
                        resizableColumns columnResizeMode="expand"
                        className="turePregledTable"
                        emptyMessage={t("admin.prodVozNema")}
                        rowClassName={r => kljucReda(r) === selectedId ? { "selected-row": true } : {}}>
                        <Column header={t("admin.izaberi")} style={{ minWidth: "90px" }}
                            body={rowData => (
                                <Button label={t("admin.izaberi")} icon="pi pi-eye" type="button"
                                        className="p-button-text p-button-sm" onClick={() => handleRowSelect(rowData)} />
                            )} />
                        <Column field="Stanje" header={t("admin.prodVozStanje")} body={r => oznakaStanja(r.Stanje)} sortable style={{ minWidth: "130px" }} />
                        <Column field="BrojFakture" header={t("admin.prodVozFaktura")} sortable style={{ minWidth: "100px" }} />
                        <Column field="DatumFakture" header={t("admin.prodVozDatum")} sortable style={{ minWidth: "110px" }} />
                        <Column field="Kupac" header={t("admin.prodVozKupac")} sortable style={{ minWidth: "180px" }} />
                        <Column field="Registracija" header={t("admin.prodVozRegistracija")} sortable style={{ minWidth: "120px" }} />
                        <Column field="Marka" header={t("admin.marka")} sortable style={{ minWidth: "110px" }} />
                        <Column field="Model" header={t("admin.model")} sortable style={{ minWidth: "110px" }} />
                        <Column field="Sasija" header={t("admin.sasija")} sortable style={{ minWidth: "170px" }} />
                        <Column field="KupacFirma" header={t("admin.prodVozKupacFirma")} sortable style={{ minWidth: "170px" }} />
                        <Column field="DatumPrenosa" header={t("admin.prodVozDatumPrenosa")} sortable style={{ minWidth: "150px" }} />
                        <Column field="Preneo" header={t("admin.prodVozPreneo")} sortable style={{ minWidth: "140px" }} />
                    </DataTable>
                </Card>
            </div>

            {/* ── Istorija vozila kroz firme ── */}
            <Dialog
                header={`${t("admin.prodVozIstorija")} — ${istorija?.naslov || ""}`}
                visible={!!istorija}
                onHide={() => setIstorija(null)}
                style={{ width: "min(640px, 95vw)" }}
                className="prodvoz-istorija-dialog"
            >
                {istorija && (
                    <>
                        {istorija.sasija && <div className="prodvoz-opis">{t("admin.sasija")}: {istorija.sasija}</div>}
                        {istorija.dogadjaji.length === 0 ? (
                            <p className="infoPoruka">{t("admin.prodVozIstorijaPrazna")}</p>
                        ) : (
                            <ol className="prodvoz-linija">
                                {istorija.dogadjaji.map((d, i) => (
                                    <li key={i} className={`prodvoz-dogadjaj ${String(d.dogadjaj || "").toLowerCase()}`}>
                                        <span className="prodvoz-tacka" />
                                        <div className="prodvoz-dogadjaj-telo">
                                            <div className="prodvoz-dogadjaj-naslov">
                                                {d.dogadjaj === "UNOS" && t("admin.prodVozDogUnos", { firma: d.Firma || "-" })}
                                                {d.dogadjaj === "PRENOS" && t("admin.prodVozDogPrenos", { od: d.Firma || "-", ka: d.Ka || "-" })}
                                                {d.dogadjaj === "ODJAVA" && t("admin.prodVozDogOdjava", { od: d.Firma || "-", ka: d.Ka || "-" })}
                                            </div>
                                            <div className="prodvoz-dogadjaj-meta">
                                                <span><i className="pi pi-clock" /> {d.Datum}</span>
                                                {d.Registracija && <span><i className="pi pi-car" /> {d.Registracija}</span>}
                                                {d.BrojFakture && <span><i className="pi pi-file" /> {t("admin.prodVozFaktura")} {d.BrojFakture}</span>}
                                                {d.Korisnik && <span><i className="pi pi-user" /> {d.Korisnik}</span>}
                                            </div>
                                            {d.Napomena && <div className="prodvoz-dogadjaj-napomena">{d.Napomena}</div>}
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </>
                )}
            </Dialog>
        </div>
    );
};

export default VozilaProdaja;
